import * as React from "react";
import "./FeaturePopup.scss";
import {ImageLoader} from "../../global/ImageLoader/ImageLoader";
import {LoaderIcon} from "../../global/LoaderIcon/LoaderIcon";
import {TweenMax} from "gsap/TweenMax";
import {Back, Linear} from "gsap/EasePack";
import {EventInjector} from "react-event-injector";
import {IFeature} from "../OverviewScreen";

export interface FeaturePopupProps {
    id: string;
    active: boolean;

    x: number;
    y: number;
    height: number;
    className: string;

    displayAsOverlay: boolean;
    mouseOverEventHandler?: (e: any, feature: IFeature) => void;

    feature: IFeature;
    imageSrc: string;
    headline: string;
    notifyLinkPress: (feature: IFeature) => void;
    notifyClosePress: (feature: IFeature) => void;
}

export class FeaturePopup extends React.Component<FeaturePopupProps, {}> {

    state = {
        alpha: this.props.active ? 1 : 0,
        scale: this.props.active ? 1 : .5,
    };

    stateAnimationProxy: any = {
        alpha: 0,
        scale: .5,
    };

    stateAnimationProxySynchronize = () => {
        this.setState(
            this.stateAnimationProxy
        );

        TweenMax.set('.FeaturePopup.' + this.props.className, {
            autoAlpha: this.state.alpha,
            scale: this.state.scale,
        });
    };

    componentWillReceiveProps(nextProps: FeaturePopupProps): void {
        if (nextProps.active != this.props.active) {
            const DUR: number = (nextProps.active) ? .3 : .15;

            TweenMax.to(this.stateAnimationProxy, DUR,
                {
                    alpha: nextProps.active ? 1 : 0,
                    scale: nextProps.active ? 1 : .5,
                    ease: Linear.easeInOut,
                    onComplete: this.stateAnimationProxySynchronize,
                    onUpdate: this.stateAnimationProxySynchronize
                }
            );
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        // only animation will be done
        // because this will be controlled by gsap, no rerender is needed
        return false;
    }

    onLinkMouseHandler = (e: any) => {
        e.preventDefault();

        switch ((e as Event).type) {
            case "mouseenter":
                TweenMax.to('.FeaturePopup .arrow', .15, {
                    right: '32px',
                    force3D: true,
                    ease: Linear.easeInOut
                });
                break;
            case "mouseleave":
                TweenMax.to('.FeaturePopup .arrow', .25, {
                    right: '39px',
                    force3D: true,
                    ease: Linear.easeInOut
                });
                break;
            case "click":
                this.props.notifyLinkPress(this.props.feature);
                break;
        }
    };
    onCloseMouseHandler = (e: any) => {
        e.preventDefault();

        switch ((e as Event).type) {
            case "mouseover":
                TweenMax.to(".closeQuad .x", .1, {scale: 0.9, force3D: true, ease: Back.easeOut.config(3)});
                break;
            case "mouseout":
                TweenMax.to(".closeQuad .x", .05, {scale: 1, force3D: true, ease: Back.easeIn});
                break;
            case "click":
                this.props.notifyClosePress(this.props.feature);
                break;
        }
    };

    mouseOverEventHandler = (evt) => {
        if (this.props.mouseOverEventHandler) {
            this.props.mouseOverEventHandler(evt, this.props.feature);
        }
    }

    render() {
        const style: any = {
            left: (!this.props.displayAsOverlay) ? this.props.x.toString() + "px" : 0,
            top: (!this.props.displayAsOverlay) ? (this.props.y - this.props.height).toString() + "px" : 0,
            height: (!this.props.displayAsOverlay) ? this.props.height.toString() + "px" : "auto",
        };

        let classNames: string[] = this.props.className.split(" ");
        if (this.props.displayAsOverlay) {
            classNames.push('display-as-overlay');
        } else {
            classNames.push('display-as-popup');
        }

        TweenMax.set('.FeaturePopup.' + this.props.className, {
            autoAlpha: this.state.alpha,
            scale: this.state.scale,
        });

        return <div
            className={'FeaturePopup ' + classNames.join(" ")}
            style={style} key={this.props.id}>
            <div className={'contentHolder'} onMouseOverCapture={this.mouseOverEventHandler}>
                <div className={'inner'}>
                    <div
                        onClick={this.onLinkMouseHandler}
                        onMouseEnter={this.onLinkMouseHandler}
                        onMouseLeave={this.onLinkMouseHandler}
                    >
                        <div className={'fp-title'}>{this.props.headline}</div>
                        <ImageLoader className={'image-loader'} src={this.props.imageSrc}>
                            <img className={'image'}/>
                            <span>Image {this.props.imageSrc} could not be loaded.</span>
                            <LoaderIcon/>
                        </ImageLoader>
                        <div className={'arrow'}>
                            <span>
                                <svg width="28px" height="53px" viewBox="0 0 11 22">
                                    <polygon fill={'#000000'} points="0,0.6 10.1,11 0,21.4 0.3,22 11,11 0.3,0 "/>
                                </svg>
                            </span>
                        </div>
                    </div>
                </div>
                <EventInjector
                    onClick={this.onCloseMouseHandler}
                    onMouseEnter={this.onCloseMouseHandler}
                    onMouseLeave={this.onCloseMouseHandler}
                    // explicity set passive:false to all events. Better set to true
                    settings={{passive: false}}
                >
                    <div className={'closeQuad'}
                         onClick={this.onCloseMouseHandler}
                         onMouseOver={this.onCloseMouseHandler}
                         onMouseOut={this.onCloseMouseHandler}
                    >
                        <svg width="24px" height="24px" viewBox="0 0 22.1 22.1">
                            <line strokeWidth={3} stroke={'#FFFFFF'} x1="1.1" y1="1.1" x2="21.1" y2="21.1"/>
                            <line strokeWidth={3} stroke={'#FFFFFF'} x1="1.1" y1="21.1" x2="21.1" y2="1.1"/>
                        </svg>
                    </div>
                </EventInjector>
            </div>
        </div>
    }
}
